import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Container, Loading, ErrorMessage, SimpleButton } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type { BeginResumeAttemptDTO, NextQuestionDTO } from './DTOs';

const StartExamPage: React.FC = () => {
    document.title = 'Assessment Attempt';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [attemptData, setAttemptData] = useState<BeginResumeAttemptDTO | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const navigate = useNavigate();
    const { attemptId } = useParams();

    // Rerender every 15 seconds to update deadline text
    const [_tick, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => {
            setTick((t) => t + 1); // Forces rerender
        }, 15000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        (async function init() {
            try {
                setError('');
                const res = await apiRequest<BeginResumeAttemptDTO>(
                    'get',
                    `assessments/studentExamView/studentExamAttemptDetails/${attemptId}`
                );
                const data = res.data;
                setAttemptData(data);

                const timeRemaining = data.timeRemaining;
                if (timeRemaining == null) {
                    setEndDate(null);
                } else {
                    const newEndDate = new Date();
                    newEndDate.setMinutes(newEndDate.getMinutes() + timeRemaining);
                    setEndDate(newEndDate);
                }
            } catch {
                setError('Failed to load attempt details.');
            } finally {
                setLoading(false);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLatestQuestionAndResume = useCallback(async () => {
        try {
            setError('');
            const res = await apiRequest<NextQuestionDTO>(
                'get',
                `assessments/studentExamView/nextQuestionId/${attemptId}`
            );
            const data = res.data;

            window.location.href = `/studentExamView/attempt/${attemptId}/q/${data.nextQuestionId}`;
        } catch {
            setError('Failed to begin/resume assessment.');
        } finally {
            setLoading(false);
        }
    }, [attemptId]);

    const isExamUnavailable =
        attemptData == null || (attemptData.timeRemaining || 1) <= 0 || attemptData.isExamCompleted;
    const isTeacherFreezeEnabled = attemptData?.isTeacherFreezeEnabled || false;

    return (
        <Container className="user-min-font-size">
            <h1 className="text-2xl font-bold mb-4">Is this correct?</h1>

            {attemptData && (
                <div>
                    <p className="mb-2">Name: {attemptData.studentName}</p>
                    <p className="mb-2">Test: {attemptData.examTitle}</p>
                    <p className="mb-2">
                        {getDeadlineText(
                            attemptData.timeRemaining,
                            attemptData.hasExamStarted,
                            attemptData.isExamCompleted,
                            endDate
                        )}
                    </p>
                    {isTeacherFreezeEnabled && (
                        <ErrorMessage error="Your teacher has enabled a freeze on this assessment. You cannot start or resume it at this time." />
                    )}
                </div>
            )}
            <br />
            {isExamUnavailable || isTeacherFreezeEnabled ? (
                <SimpleButton onClick={() => navigate(-1)}>Back</SimpleButton>
            ) : (
                <SimpleButton
                    color="success"
                    onClick={getLatestQuestionAndResume}
                    disabled={loading}
                >
                    Yes, Start My Test
                </SimpleButton>
            )}

            <Loading visible={loading} size="lg" />
            <ErrorMessage error={error} />
        </Container>
    );
};

function getDeadlineText(
    timeRemaining: number | null,
    hasExamStarted: boolean,
    isExamCompleted: boolean,
    endDate: Date | null
): string {
    if (isExamCompleted) return 'This assessment has already been submitted.';
    if (!endDate) return 'This assessment does not have a time limit.';

    const formattedDate = new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(endDate);

    if (endDate < new Date()) return `This assessment ended at ${formattedDate}.`;

    if (!hasExamStarted) {
        return `You have ${timeRemaining} minute${timeRemaining === 1 ? '' : 's'} to complete the assessment once you begin.`;
    }

    const now = new Date();
    const diff = Math.max(endDate.getTime() - now.getTime(), 0);
    const diffInMinutes = Math.ceil(diff / (1000 * 60));

    return `Test ends at ${formattedDate} (${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} remaining).`;
}

export default StartExamPage;
